<template>
  <div ref="container" fill-height style="display: inline-block">
    <svg
      ref="chart"
      :width="width"
      :height="height"
      style="overflow: visible; z-index: 5;"
    >
      <g
        v-yaxis="yAxis"
        :transform="
          `translate(${left ? width - margin.right : margin.left}, ${
            margin.top
          })`
        "
      ></g>
      <g ref="bars" :transform="`translate(${margin.left}, ${margin.top})`">
        <title v-if="hasSelection()">
          Click outside the selected area to remove the filter.
        </title>
        <title v-else-if="interactive">
          Click and drag to add a cohort filter.
        </title>
        <!-- Population bars are first so they will hide under cohort bars -->
        <rect
          v-for="(bin, i) in popBins"
          :key="`population-${i}`"
          :x="left ? xScale(bin.length) : 1"
          :y="
            axisFlippedNotNull && axisFlipped ? yScale(bin.x0) : yScale(bin.x1)
          "
          :height="
            Math.abs(yScale(bin.x0) - yScale(bin.x1)) - 1 > 0
              ? Math.abs(yScale(bin.x0) - yScale(bin.x1)) - 1
              : 0
          "
          :width="
            left
              ? w - xScale(bin.length) > 0
                ? w - xScale(bin.length)
                : 0
              : xScale(bin.length) > 0
              ? xScale(bin.length)
              : 0
          "
          :fill="colors['population']"
          :opacity="getOpacity('population')"
        />
        <!-- Plot population minus selected cohort -->
        <g v-if="highlightedSubset === 'non-cohort'">
          <rect
            v-for="(bin, i) in popMinusBins"
            :key="`pop-minus-cohort-${i}`"
            :x="getNonCohortX(left, bin)"
            :y="
              axisFlippedNotNull && axisFlipped
                ? yScale(bin.x0)
                : yScale(bin.x1)
            "
            :height="
              Math.abs(yScale(bin.x0) - yScale(bin.x1)) - 1 > 0
                ? Math.abs(yScale(bin.x0) - yScale(bin.x1)) - 1
                : 0
            "
            :width="getNonCohortWidth(left, bin)"
            :fill="colors['nonCohort']"
            :opacity="getOpacity('non-cohort')"
          />
        </g>
        <g v-if="highlightedSubset === 'cohort'">
          <rect
            v-for="(bin, i) in bins"
            :key="`cohort-${i}`"
            :x="getCohortX(left, bin)"
            :y="
              axisFlippedNotNull && axisFlipped
                ? yScale(bin.x0)
                : yScale(bin.x1)
            "
            :height="
              Math.abs(yScale(bin.x0) - yScale(bin.x1)) - 1 > 0
                ? Math.abs(yScale(bin.x0) - yScale(bin.x1)) - 1
                : 0
            "
            :width="getCohortWidth(left, bin)"
            :fill="colors['cohort']"
            :opacity="getOpacity('cohort')"
          />
        </g>

        <g v-if="interactive" ref="brush" class="brush"></g>

        <!-- Cohort Mean -->
        <g v-if="typeof meanY !== 'undefined'">
          <circle
            r="10"
            :cx="left ? w : 0"
            :cy="meanY"
            :fill="colors['cohort-circle-fill']"
            :stroke="colors['cohort-circle-stroke']"
            :stroke-width="colors['cohort-circle-stroke-width']"
            :fill-opacity="colors['cohort-circle-fill-opacity']"
          ></circle>

          <g class="tooltip2 css">
            <rect
              :x="left ? w - 240 : -150"
              :y="meanY - 50"
              width="240"
              height="2em"
              fill="#3f51b5"
              rx="5"
              ry="5"
            />
            <text
              :x="(left ? w : 90) - 120"
              :y="meanY - 50"
              dy="1.2em"
              text-anchor="middle"
              fill="white"
            >
              Cohort Mean: {{ mean }}&nbsp;&#177;&nbsp;{{ stddev }}
            </text>
          </g>
        </g>
        <!-- Population Mean -->
        <g v-if="typeof populationMeanY !== 'undefined'">
          <circle
            r="10"
            :cx="left ? w : 0"
            :cy="populationMeanY"
            :fill="colors['population-circle-fill']"
            :stroke="colors['population-circle-stroke']"
            :stroke-width="colors['population-circle-stroke-width']"
            :fill-opacity="colors['population-circle-fill-opacity']"
          ></circle>
          <g class="tooltip2 css">
            <rect
              :x="left ? w - 240 : -150"
              :y="populationMeanY - 50"
              width="240"
              height="2em"
              fill="#3f51b5"
              rx="5"
              ry="5"
            />
            <text
              :x="(left ? w : 90) - 120"
              :y="populationMeanY - 50"
              dy="1.2em"
              text-anchor="middle"
              fill="white"
            >
              Population Mean: {{ populationMean }}&nbsp;&#177;&nbsp;{{
                populationStddev
              }}
            </text>
          </g>
        </g>
      </g>

      <text
        v-if="axisFlippedNotNull"
        :x="left ? margin.left : width - margin.right"
        :y="margin.top + 15"
        :text-anchor="left ? 'start' : 'end'"
        font-size="0.8em"
      >
        worse
      </text>
      <text
        v-if="axisFlippedNotNull"
        :x="left ? margin.left : width - margin.right"
        :y="height - margin.bottom + 4"
        :text-anchor="left ? 'start' : 'end'"
        font-size="0.8em"
      >
        better
      </text>

      <g
        v-xaxis="populationXAxis"
        :transform="`translate(${margin.left}, ${margin.top})`"
      ></g>
    </svg>
  </div>
</template>

<script>
// Data Store
import { mapState, mapActions, mapGetters } from 'vuex';
import { state, actions, getters } from '@/store/modules/cohortManager/types';
// D3 Modules
import { extent, max, mean, median, deviation, histogram } from 'd3-array';
import { brushY } from 'd3-brush';
import { select, event } from 'd3-selection';
import { scaleLinear } from 'd3-scale';
import { format } from 'd3-format';
import 'd3-transition';
import { axisTop, axisLeft, axisRight } from 'd3-axis';
// Directives
import resize from 'vue-resize-directive';
// Components
// import AnimatedRect from './HistogramBar.vue';
import { colors } from '@/utils/colors';

/**
 * Takes an array of key, value counts from crossfilter groups
 * and expands then flattens them. For example, [{key:5, value: 3}, {key:2, value: 2}]
 * would be flattened to [5,5,5,2,2]. This allows us to leverage d3's
 * histrogram/binning abilities.
 */
const flattenGroupCounts = data => {
  let res = [];
  data.forEach(d => {
    for (let i = 0; i < d.value; i++) {
      res.push(d.key);
    }
  });
  return res;
};

export default {
  directives: {
    resize,
    xaxis(el, binding) {
      const axisMethod = binding.value;
      select(el)
        .transition()
        .call(axisMethod);
      // .call(g => g.select('.domain').remove());
    },
    yaxis(el, binding) {
      const axisMethod = binding.value;
      select(el)
        .transition()
        .call(axisMethod);
      // .call(g => g.select('.domain').remove());
    },
  },
  components: {
    //    AnimatedRect,
  },
  props: {
    id: {
      type: [Number, String],
      required: true,
    },
    dimensionName: {
      type: String,
      required: true,
    },
    left: {
      type: Boolean,
      default: false,
    },
    yMin: {
      type: Number,
      required: true,
    },
    yDomain: {
      type: Number,
      required: true,
    },
    nYvals: {
      type: Number,
      required: true,
    },
    variable: {
      type: Object,
      required: true,
    },
    interactive: {
      type: Boolean,
      required: false,
      default: true,
    },
    manualMin: {
      type: Number,
      required: false,
      default: 0,
    },
    manualMax: {
      type: Number,
      required: false,
      default: 0,
    },
    predefRadio: {
      type: String,
      required: false,
      default: null,
    },
    paddedMinValue: {
      type: Number,
      required: false,
      default: null,
    },
    paddedMaxValue: {
      type: Number,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      isLoading: true,
      haveDimensions: false,
      width: 0,
      height: 0,
      filter: null,
      selected: [],
      container: null,
      dimension: null,
      group: [],
      data: [],
      populationData: [],
      selection: [],
      selectedRange: null,
      selectedPopSubset: null,
      meanY: null,
      populationMeanY: null,
      colors: colors,
    };
  },
  computed: {
    ...mapGetters('cohortManager', {
      getQuery: getters.GET_QUERY,
      findCohortQuery: getters.FIND_COHORT_QUERY,
      hasUserSelectedCohort: getters.HAS_USER_SELECTED_COHORT,
    }),
    ...mapState('cohortManager', {
      filteredData: state.FILTERED_DATA,
      unfilteredData: state.UNFILTERED_DATA,
      dimensions: state.DIMENSIONS,
      highlightedSubset: state.HIGHLIGHTED_SUBSET,
      cohort: state.COHORT,
    }),
    num_bins() {
      let n_bins = this.yDomain - this.yMin + 1;
      if (this.nYvals < 30) return this.nYvals;
      return n_bins > 30 ? 30 : n_bins;
    },
    margin() {
      return {
        top: 20,
        right: this.left ? 32 : 10,
        bottom: 10,
        left: this.left ? 10 : 35,
      };
    },
    w() {
      const { left, right } = this.margin;
      const { width } = this;
      return width - left - right;
    },
    h() {
      const { top, bottom } = this.margin;
      const { height } = this;
      return height - top - bottom;
    },
    // colorScale() {
    //   return scaleOrdinal()
    //     .domain(this.data.map(c => c.key))
    //     .range(schemeCategory10);
    // },
    yScale() {
      return scaleLinear()
        .domain([this.yMin, this.yDomain])
        .range(
          this.axisFlippedNotNull && this.axisFlipped
            ? [0, this.h]
            : [this.h, 0]
        );
    },
    cohortXScale() {
      return scaleLinear()
        .domain(extent(this.data))
        .range([0, this.w]);
    },
    hist() {
      return histogram()
        .value(d => +d)
        .domain(this.yScale.domain())
        .thresholds(this.yScale.ticks(this.num_bins));
    },
    populationCounts() {
      var populationCounts = {};
      var popBins = this.popBins;
      const pbLen = popBins.length;
      for (let i = 0; i < pbLen; i += 1) {
        populationCounts[popBins[i].x0] = popBins[i].length;
      }
      return populationCounts;
    },
    popBins() {
      return this.hist(this.populationData);
    },
    bins() {
      return this.hist(this.data);
    },
    popMinusBins() {
      // subtract bins from population bins to get popMinusBins
      // i.e., bins for the non-cohort population
      const pb = this.hist(this.populationData);
      const b = this.hist(this.data);
      const pmBins = [];
      const pbLen = pb.length;

      // TODO - compute this in a more straightforward fashion
      // TODO - factor out code in common with HistogramChart.vue
      for (let i = 0; i < pbLen; i += 1) {
        const nbl = [];
        nbl.x0 = pb[i].x0;
        nbl.x1 = pb[i].x1;
        const bl = typeof b[i] !== 'undefined' ? b[i].length : 0;
        const pbl = typeof pb[i] !== 'undefined' ? pb[i].length : 0;
        // TODO - we're creating bins of the correct size for display
        // purposes, but the values inside aren't correct.
        for (let j = bl; j < pbl; j += 1) {
          nbl.push(1);
        }
        pmBins.push(nbl);
      }
      return pmBins;
    },
    xScale() {
      const xScale = scaleLinear()
        .domain([0, max(this.popBins, d => d.length)])
        .range(this.left ? [this.w, 0] : [0, this.w]);
      return xScale;
    },
    xAxis() {
      return axisTop(this.xScale);
    },
    populationXAxis() {
      return axisTop(this.xScale).ticks(3);
    },
    yAxis() {
      const axis = this.left ? axisRight : axisLeft;
      return axis(this.yScale);
    },
    brush() {
      return brushY()
        .extent([[0, 0], [this.w, this.h]])
        .on('start brush', this.brushed)
        .on('end', this.brushedData);
    },
    mean() {
      return format('.1~f')(
        mean(this.filteredData.map(this.dimension.accessor))
      );
    },
    median() {
      return format('.1~f')(
        median(this.filteredData.map(this.dimension.accessor))
      );
    },
    stddev() {
      return format('.1~f')(
        deviation(this.filteredData.map(this.dimension.accessor))
      );
    },
    populationMean() {
      return format('.1~f')(mean(this.populationData));
    },
    populationMedian() {
      return format('.1~f')(median(this.populationData));
    },
    populationStddev() {
      return format('.1~f')(deviation(this.populationData));
    },
    axisFlipped() {
      return this.variable && this.variable.flip_axis;
    },
    axisFlippedNotNull() {
      return (
        this.variable &&
        this.variable.type == 'observation' &&
        this.variable.flip_axis != null
      );
    },
  },
  watch: {
    dimensionName() {
      const dimension = this.dimensions[this.dimensionName];
      this.dimension = dimension;
      this.populationData = this.unfilteredData.map(dimension.accessor);
      this.group = dimension.group();
      this.data = flattenGroupCounts(this.group.all());
      this.updateRangeFromCohortQuery();
      this.updateRangeFromQuery();
    },
    manualMin() {
      this.manualSelection();
    },
    manualMax() {
      this.manualSelection();
    },
    filteredData() {
      this.data = flattenGroupCounts(this.group.all());
      this.updateSelected();
      this.updateMeanY();
      this.updateRangeFromQuery();
    },
    selectedRange(s) {
      if (s == null) {
        return;
      }
      this.selectedPopSubset = null;
      this.selectRange(s);
    },
    selectedPopSubset(s) {
      if (s == null) {
        return;
      }
      this.selectedRange = null;
      this.selectRange(s);
      this.$emit('userChangedVariable', this.dimension);
    },
    selected(newSel) {
      // prevent loop
      if (!newSel.length) {
        return;
      }

      // check whether selection has been cleared, remove brush if so
      if (this.hasSelection()) {
        // assess whether the filter on our dimension has been cleared
        const dim = this.dimensions[this.dimensionName];
        if (typeof dim === 'undefined') {
          this.clearSelection();
        } else {
          const cf = dim.currentFilter();
          if (typeof cf === 'undefined') {
            this.clearSelection();
          }
        }
      }
      this.updateMeanY();
    },
    populationData() {
      this.updatePopulationMeanY();
    },
    cohort() {
      this.updateRangeFromCohortQuery();
    },
  },
  created() {
    const dimension = this.dimensions[this.dimensionName];
    this.dimension = dimension;
    this.populationData = this.unfilteredData.map(dimension.accessor);
    this.group = dimension.group();
    this.data = flattenGroupCounts(this.group.all());
    this.updateRangeFromCohortQuery();
    this.updateRangeFromQuery();
  },
  mounted() {
    this.container = this.$refs.container;
    // Resize chart so we have parent dimensions (width/height)
    this.resizeChart();
    this.initializeBrush();
  },
  methods: {
    ...mapActions('cohortManager', {
      addFilter: actions.ADD_FILTER,
      clearFilter: actions.CLEAR_FILTER,
    }),
    updateRangeFromCohortQuery() {
      const [query] = this.findCohortQuery(this.dimensionName);
      if (typeof query !== 'undefined') {
        this.$nextTick(() => {
          let minValue =
            query.min_value >= this.yMin ? query.min_value : this.yMin;
          let maxValue =
            query.max_value <= this.yDomain ? query.max_value : this.yDomain;

          // Filter dimension to be within selection
          let filter = {
            dimension: this.dimensionName,
            filter: d => d >= minValue && d < maxValue,
            query: {
              minValue: minValue,
              maxValue: maxValue,
            },
          };

          this.addFilter(filter);
          this.filter = filter;
        });
      }
    },
    updateRangeFromQuery() {
      const query = this.getQuery(this.dimensionName);

      if (query == null || typeof query == 'undefined') {
        this.clearSelection();
        this.$emit('updateRangeFromQuery', null, null, null, null);
      } else {
        this.$nextTick(() => {
          let minValue =
            query[0].minValue >= this.yMin ? query[0].minValue : this.yMin;
          let maxValue =
            query[0].maxValue <= this.yDomain
              ? query[0].maxValue
              : this.yDomain;

          if (this.manualMin != minValue || this.manualMax != maxValue) {
            this.$emit(
              'updateRangeFromQuery',
              minValue,
              maxValue,
              this.selectedRange,
              this.selectedPopSubset
            );
          }
        });
      }
    },
    manualSelection() {
      if (this.manualMin == null && this.manualMax == null) {
        this.clearSelection();
      } else if (
        this.manualMin < this.yMin ||
        this.manualMax > this.yDomain ||
        this.manualMin > this.manualMax ||
        this.manualMin == this.manualMax ||
        this.manualMin > this.yDomain ||
        this.manualMax < this.yMin
      ) {
        // do nothing
      } else {
        select(this.$refs.brush)
          .transition()
          .call(this.brush.move, [
            this.yScale(this.manualMax),
            this.yScale(this.manualMin),
          ]);

        // Filter dimension to be within snapped selection
        if (this.filter == null) {
          let filter = {
            dimension: this.dimensionName,
            filter: d => d >= this.manualMin && d < this.manualMax,
            query: {
              minValue: this.manualMin,
              maxValue: this.manualMax,
            },
          };

          this.addFilter(filter);
          // assumes at most 1 filter, which appears to be the case
          this.filter = filter;
        }
        this.updateSelected();
      }
    },
    clearSelection() {
      if (this.handle) {
        this.handle.attr('display', 'none');
      }
      select(this.$refs.brush).call(this.brush.move, null);
      this.selected = [];
      this.selection = [];
      this.filter = null;
    },
    updateSelected() {
      if (this.hasSelection()) {
        const f = this.filter;
        const accFn = this.dimension.accessor;
        const filtered = this.filteredData.filter(d => {
          const datum = accFn(d);
          return f.filter(datum);
        });
        this.selected = filtered.map(accFn);
      }
    },
    updateMeanY() {
      // current selection (if any) must be taken into account
      if (this.hasSelection()) {
        this.meanY = this.yScale(mean(this.selected));
      } else {
        this.meanY = this.yScale(mean(this.data));
      }
    },
    updatePopulationMeanY() {
      this.populationMeanY = this.yScale(mean(this.populationData));
    },
    userChangedVariable() {
      this.$emit('userChangedVariable', this.dimensionName);
    },
    initializeBrush() {
      const brushEl = this.$refs.brush;

      const brush = select(brushEl).call(this.brush);
      select(brushEl)
        .select('.selection')
        .attr('fill', '#3F51B5')
        .attr('fill-opacity', 0.1);

      // This provides the shape of our handles on the brush
      const handlePath = d => {
        const e = +(d.type === 'e');
        const x = e ? 1 : -1;
        const y = this.w;
        return `M${0.5 * x},${y}A6,6 0 0 ${e} ${6.5 * x},${y + 6}V${2 * y -
          6}A6,6 0 0 ${e} ${0.5 * x},${2 * y}ZM${2.5 * x},${y + 8}V${2 * y -
          8}M${4.5 * x},${y + 8}V${2 * y - 8}`;
      };

      const handle = brush
        .selectAll('.handle--custom')
        .data([{ type: 'w' }, { type: 'e' }])
        .enter()
        .append('path')
        .attr('class', 'handle--custom')
        .attr('fill', '#3F51B5')
        .attr('fill-opacity', 0.8)
        .attr('stroke', '#FFF')
        .attr('stroke-width', 1)
        .attr('cursor', 'ew-resize')
        .attr('d', handlePath)
        .attr('display', 'none');
      this.handle = handle;
    },
    /**
     * Gets the closest cohort bins to a selection so brush can appropriately
     * snap to the correct locations.
     */
    getClosestBins() {
      const { selection } = event;
      const [upper, lower] = selection.map(this.yScale.invert);

      var x1fn = x =>
        this.axisFlippedNotNull && this.axisFlipped ? x.x0 : x.x1;
      var x0fn = x =>
        this.axisFlippedNotNull && this.axisFlipped ? x.x1 : x.x0;

      // Get closest bin to our upper selection (i.e., higher up on the screen)
      const closestBinToUpper = this.bins
        .map(bin => x1fn(bin))
        .map(x => Math.abs(x - upper))
        .reduce((acc, currVal) => Math.min(acc, currVal));
      const newUpperIdx = this.bins.findIndex(
        bin => Math.abs(x1fn(bin) - upper) === closestBinToUpper
      );
      const newUpper = x1fn(this.bins[newUpperIdx]);

      // Get closest bin to our lower selection (i.e., lower down on the screen)
      const closestBinToLower = this.bins
        .map(bin => x0fn(bin))
        .map(x => Math.abs(x - lower))
        .reduce((acc, currVal) => Math.min(acc, currVal));
      const newLowerIdx = this.bins.findIndex(
        bin => Math.abs(x0fn(bin) - lower) === closestBinToLower
      );
      const newLower = x0fn(this.bins[newLowerIdx]);
      return [newUpper, newLower].map(this.yScale);
    },
    brushedData() {
      // Only transition after input.
      if (!event.sourceEvent) return;
      // Ignore empty selections.
      if (!event.selection) {
        this.selected = [];
        this.clearFilter({
          dimension: this.dimensionName,
        });
        this.filter = null;
        return;
      }
      const [high, low] = this.getClosestBins();

      // Snap selections to closest bins
      select(this.$refs.brush)
        .transition()
        .call(event.target.move, [high, low]);

      var [invertedHigh, invertedLow] = [high, low].map(this.yScale.invert);

      if (invertedLow > invertedHigh) {
        var tmp = invertedLow;
        invertedLow = invertedHigh;
        invertedHigh = tmp;
      }

      // workaround for when entire range is selected
      var last_bin = this.bins[this.bins.length - 1];
      if (last_bin.x0 == last_bin.x1) {
        last_bin = this.bins[this.bins.length - 2];
      }
      if (invertedHigh >= last_bin.x1) {
        // add a small amount so nobody notices
        invertedHigh += (last_bin.x1 - last_bin.x0) * 0.1;
      }

      // Filter dimension to be within snapped selection
      const newFilter = {
        dimension: this.dimensionName,
        filter: d => d >= invertedLow && d < invertedHigh,
        query: {
          minValue: invertedLow,
          maxValue: invertedHigh,
        },
      };
      this.addFilter(newFilter);
      // assumes at most 1 filter, which appears to be the case
      this.filter = newFilter;
      this.updateSelected();
      this.$emit('userChangedVariable', this.dimensionName);
      this.$emit('userChangedFilter', this.filter);
    },
    brushed() {
      const { selection } = event;
      if (!selection) {
        this.selection = [];
        return; // Ignore empty selections
      }

      const [low, high] = selection;
      // Ignore selections with no range
      if (high - low === 0) {
        this.handle.attr('display', 'none');
        select(this.$refs.brush).call(this.brush.move, null);
        this.selection = [];
        return;
      }

      // Appropriately place brush handles
      var tx = this.w * 2;
      this.handle
        .attr('display', null)
        .attr(
          'transform',
          (d, i) => `translate(${tx},${selection[i]}) rotate(90)`
        );

      // Set remap our selection to snap to closest bins
      this.selection = this.getClosestBins();
    },
    getFill(bin) {
      if (this.hasSelection()) {
        if (this.isBinSelected(bin)) {
          return '#3F51B5';
        }
        return '#E8EAF6';
      }
      return '#3F51B5';
    },
    hasSelection() {
      if ((this.selection && this.selection.length) || this.filter) {
        return true;
      }
      return false;
    },
    isBinSelected(bin) {
      var [high, low] = this.selection.map(this.yScale.invert);
      if (low > high) {
        var tmp = low;
        low = high;
        high = tmp;
      }
      const { x0, x1 } = bin;

      // padding must be adaptive - set to half the bin width/height
      const PADDING = (x1 - x0) / 2.0;
      // Check that bin is within selection range
      if (low - PADDING <= x0 && high + PADDING >= x1) {
        return true;
      }
      return false;
    },
    getCohortX(left, bin) {
      const x = left ? this.xScale(bin.length) : 0;
      if (this.hasSelection()) {
        if (this.isBinSelected(bin)) {
          return x;
        }
        return left ? this.xScale(0) : 0;
      }
      return x;
    },
    getCohortWidth(left, bin) {
      const barWidth = left
        ? this.w - this.xScale(bin.length) > 0
          ? this.w - this.xScale(bin.length)
          : 0
        : this.xScale(bin.length) > 0
        ? this.xScale(bin.length)
        : 0;

      if (this.hasSelection()) {
        if (this.isBinSelected(bin)) {
          return barWidth;
        }
        return 0;
      }
      return barWidth;
    },
    getNonCohortX(left, bin) {
      const x = left ? this.xScale(bin.length) : 0;
      if (this.hasSelection()) {
        if (this.isBinSelected(bin)) {
          return x;
        }
        const binLen = this.populationCounts[bin.x0];
        return left ? this.xScale(binLen) : 0;
      }
      return x;
    },
    getNonCohortWidth(left, bin) {
      const barWidth = left
        ? this.w - this.xScale(bin.length) > 0
          ? this.w - this.xScale(bin.length)
          : 0
        : this.xScale(bin.length) > 0
        ? this.xScale(bin.length)
        : 0;

      if (this.hasSelection()) {
        if (this.isBinSelected(bin)) {
          return barWidth;
        }
        const binLen = this.populationCounts[bin.x0];
        return left
          ? this.w - this.xScale(binLen) > 0
            ? this.w - this.xScale(binLen)
            : 0
          : this.xScale(binLen) > 0
          ? this.xScale(binLen)
          : 0;
      }
      return barWidth;
    },
    getOpacity() {
      // if (this.interactive) {
      //   return 1;
      // }
      // else {
      //   return 0.8;
      // }
      return 1;
    },
    resizeChart() {
      this.height = 300;
      this.width = 120;
    },
    getGenMedian(data, n, d) {
      let len = data.length;
      let pivot = (len / d) * n;
      let pm = pivot - 0.5;
      let pp = pivot + 0.5;
      let pmi = Math.floor(pm);
      let ppi = Math.floor(pp);
      let pp_frac = pp - ppi;
      let pm_frac = 1.0 - pp_frac;
      return parseFloat(data[pmi] * pm_frac + data[ppi] * pp_frac).toFixed(1);
    },
    selectRange(s) {
      let minValue = Number(s.min);
      let maxValue = Number(s.max);
      const newFilter = {
        dimension: this.dimensionName,
        filter: d => d >= minValue && d < maxValue,
        query: {
          minValue,
          maxValue,
        },
      };
      this.addFilter(newFilter);
      this.filter = newFilter;
    },
    popSubsetItems() {
      var items = [];
      let popData = this.populationData.slice().sort((a, b) => a - b);

      let median = this.getGenMedian(popData, 1, 2);
      let qtr1 = this.getGenMedian(popData, 1, 4);
      let qtr3 = this.getGenMedian(popData, 3, 4);
      let thr1 = this.getGenMedian(popData, 1, 3);
      let thr2 = this.getGenMedian(popData, 2, 3);
      let lower = this.paddedMinValue;
      let upper = this.paddedMaxValue;

      if (this.predefRadio == 'halves') {
        items.push({ label: 'bottom 1/2', min: lower, max: median });
        items.push({ label: 'top 1/2', min: median, max: upper });
      } else if (this.predefRadio == 'tertiles') {
        items.push({ label: 'bottom 1/3', min: lower, max: thr1 });
        items.push({ label: 'middle 1/3', min: thr1, max: thr2 });
        items.push({ label: 'top 1/3', min: thr2, max: upper });
      } else if (this.predefRadio == 'quartiles') {
        items.push({ label: '1st quartile', min: lower, max: qtr1 });
        items.push({ label: '2nd quartile', min: qtr1, max: median });
        items.push({ label: '3rd quartile', min: median, max: qtr3 });
        items.push({ label: '4th quartile', min: qtr3, max: upper });
      }
      return items;
    },
    getCohortRangeEndpoint(ep, ext, data) {
      // fudge endpoints - 0.1 because the database uses decimal(11,2) in cohort_query.min/max_value
      if (ep == 'min') {
        return ext[0] - 0.01;
      } else if (ep == 'max') {
        return ext[1] + 0.01;
      } else {
        return this.getGenMedian(data, ep[0], ep[1]);
      }
    },
    selectCohortRange(ss, t) {
      var data = null;
      // population
      if (t.startsWith('study population')) {
        data = this.populationData;
      }
      // cohort
      else {
        data = this.data;
      }
      let ext = extent(data);
      let sorted_d = data.slice().sort((a, b) => a - b);
      var min = this.getCohortRangeEndpoint(ss.min, ext, sorted_d);
      var max = this.getCohortRangeEndpoint(ss.max, ext, sorted_d);
      this.selectRange({ min, max });
    },
    getPredefCohorts() {
      var ranges = null;
      if (this.predefRadio == 'halves') {
        ranges = [
          { label: 'Bottom 1/2', min: 'min', max: [1, 2] },
          { label: 'Top 1/2', min: [1, 2], max: 'max' },
        ];
      } else if (this.predefRadio == 'tertiles') {
        ranges = [
          { label: 'Bottom 1/3', min: 'min', max: [1, 3] },
          { label: 'Middle 1/3', min: [1, 3], max: [2, 3] },
          { label: 'Top 1/3', min: [2, 3], max: 'max' },
        ];
      } else if (this.predefRadio == 'quartiles') {
        ranges = [
          { label: '1st quartile', min: 'min', max: [1, 4] },
          { label: '2nd quartile', min: [1, 4], max: [1, 2] },
          { label: '3rd quartile', min: [1, 2], max: [3, 4] },
          { label: '4th quartile', min: [3, 4], max: 'max' },
        ];
      }
      var cohorts = [];
      // NOTE - set to this.data to include any other extant filters
      var rawData = this.unfilteredData;
      var data = rawData.map(d => this.dimension.accessor(d));
      let ext = extent(data);
      let sorted_d = data.slice().sort((a, b) => a - b);

      ranges.forEach(r => {
        var min = this.getCohortRangeEndpoint(r.min, ext, sorted_d);
        var max = this.getCohortRangeEndpoint(r.max, ext, sorted_d);

        // apply filter to produce cohort
        var filtered_d = rawData.filter(d => {
          let dv = this.dimension.accessor(d);
          return dv >= min && dv < max;
        });

        var cohort = {
          label: r.label,
          dimension: this.dimensionName,
          query_string: this.variable.abbreviation + ' ' + min + ' - ' + max,
          data: filtered_d,
          subject_ids: filtered_d.map(d => d.subject_id),
          range: { min: min, max: max },
        };

        cohorts.push(cohort);
      });

      return cohorts;
    },
    getPredefinedRanges() {
      return {
        dimension: this.dimensionName,
        cohorts: this.getPredefCohorts(),
        name: this.predefRadio,
        select_fn: this.selectCohortRange,
        dimension_name: this.variable.abbreviation,
        variable: this.variable,
      };
    },
    getTooltipX(x, tWidth) {
      let newX = x;
      if (newX < -this.margin.left) {
        newX = -this.margin.left;
      } else if (newX > this.width - this.margin.left - tWidth) {
        newX = this.width - this.margin.left - tWidth;
      }
      return newX;
    },
  },
};
</script>

<style lang="scss" scoped>
.center-text >>> input {
  text-align: center;
}

circle:hover + g.tooltip2.css {
  opacity: 1;
}

.tooltip2 {
  pointer-events: none; /*let mouse events pass through*/
  opacity: 0;
  // transition: opacity 0.3s;
  text-shadow: 1px 1px 0px gray;
}

g.tooltip2 rect {
  fill: '#3f51b5';
}
</style>
